import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useStorage } from '@vueuse/core';
import { useAuthStore } from './auth';

export interface LinkedCompany {
  id: number;
  name: string;
  // core360_auth's company_config table - whether this company requires
  // warehouse requisitioning, bill management, per-project expense tracking,
  // and job card linking, respectively. Read off selectedCompany for
  // processing and calculations; always present (the columns are NOT NULL,
  // and the backend defaults an unresolvable company to false).
  whseReq: boolean;
  billReq: boolean;
  projects: boolean;
  jobcardReq: boolean;
}

// Persisted per-browser via useStorage for the duration of a single launch
// (so the pick sticks across in-app navigation and a manual page refresh)
// but NOT across launches: fetchCompanies below always clears it back to
// null on boot when more than one company is linked, so the CompanySelectDialog
// blocks every fresh launch rather than silently reusing a past pick - per
// explicit request that selecting a company is a conscious, per-launch
// choice. Per-module storage (this key lives on this module's own origin
// only) - deliberately not shared with hr/pos/ea, which each manage their
// own independently.
export const useCompanyStore = defineStore('company', () => {
  const companies = ref<LinkedCompany[]>([]);
  const selectedCompanyId = useStorage<number | null>('core360-procurement-selected-company-id', null);
  const loading = ref(false);
  const loaded = ref(false);
  const error = ref<string | null>(null);
  // True once fetchCompanies confirms the caller has at least one
  // user_companies row - kept separate from companies.length, which can be
  // zero either because of that, or because none of those companies carry
  // any procurement permission. Those need different messaging (see
  // noLinkedCompanies/noPermittedCompanies below).
  const hasAnyMembership = ref(false);

  const selectedCompany = computed(
    () => companies.value.find((c) => c.id === selectedCompanyId.value) ?? null,
  );

  // Fetched successfully but the caller has no linked companies at all - a
  // real, blocking problem (there's nothing to select and no legitimate
  // company database to use), not a "nothing to do here" state.
  const noLinkedCompanies = computed(
    () => loaded.value && !error.value && !hasAnyMembership.value,
  );

  // Distinct from noLinkedCompanies: the caller IS linked to at least one
  // company, but none of them carry a procurement permission - a
  // permissions-grant problem, not a membership one, and needs its own
  // message so the user (and whoever they contact) isn't sent looking for
  // the wrong fix.
  const noPermittedCompanies = computed(
    () => loaded.value && !error.value && hasAnyMembership.value && companies.value.length === 0,
  );

  // App content (router-view) stays hidden until this is true - i.e. until a
  // company is actually selected. Deliberately keyed off selectedCompanyId
  // itself, not "loaded && no selection needed": companies.length === 0 must
  // never count as ready, or every company-scoped request silently sends no
  // X-Company-Id and the backend's resolveCompanyDb silently falls back to
  // its legacy hardcoded database - wrong data with no error anywhere.
  const ready = computed(() => loaded.value && !error.value && selectedCompanyId.value !== null);

  // Drives the blocking CompanySelectDialog shown on launch: a caller linked
  // to more than one company and with no selection resolved yet (locally or
  // via the cross-module shared pick) must choose before proceeding, rather
  // than silently defaulting to the first company.
  const needsSelection = computed(
    () => loaded.value && !error.value && companies.value.length > 1 && selectedCompanyId.value === null,
  );

  // Writes through to core360_auth (via this module's own backend) so every
  // other module opens to the same company by default too, instead of each
  // one remembering its own choice independently. Fire-and-forget: a failed
  // write only means the NEXT app open won't see this pick as the shared
  // default - it doesn't block using the company locally right now.
  function selectCompany(id: number) {
    selectedCompanyId.value = id;
    void persistSelection(id);
  }

  async function persistSelection(id: number) {
    try {
      const token = useAuthStore().token;
      const res = await fetch('/api/companies/selected', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ companyId: id }),
      });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
    } catch (err) {
      console.error('[Company] Failed to persist selected company:', err);
    }
  }

  // Linked companies live in core360_auth's user_companies, not in this
  // module - see core360_auth's CORE360_ACCESS_BRIEF.md §3a. Fetched through
  // this module's own backend (GET /api/companies/mine), which resolves the
  // display name from RS_Staging.
  async function fetchCompanies() {
    loading.value = true;
    error.value = null;
    try {
      const token = useAuthStore().token;
      const res = await fetch('/api/companies/mine', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      const data = (await res.json()) as {
        companies: LinkedCompany[];
        hasAnyMembership: boolean;
        selectedCompanyId: number | null;
      };
      companies.value = data.companies;
      hasAnyMembership.value = data.hasAnyMembership;

      if (companies.value.length === 1) {
        // Only one company to create this requisition for - nothing to
        // choose, so skip straight to it instead of showing the picker.
        selectCompany(companies.value[0]!.id);
      } else {
        // More than one company linked - always require an explicit pick on
        // this launch, ignoring any previously remembered selection (this
        // module's own localStorage, or the cross-module shared pick in
        // core360_auth). Per explicit request: switching company is a
        // conscious, per-launch choice, not something silently carried over.
        selectedCompanyId.value = null;
      }
    } catch (err) {
      console.error('[Company] Failed to fetch linked companies:', err);
      error.value = 'Unable to load your linked companies.';
    } finally {
      loaded.value = true;
      loading.value = false;
    }
  }

  function reset() {
    companies.value = [];
    hasAnyMembership.value = false;
    selectedCompanyId.value = null;
    loaded.value = false;
    error.value = null;
  }

  return {
    companies,
    selectedCompanyId,
    selectedCompany,
    loading,
    loaded,
    error,
    noLinkedCompanies,
    noPermittedCompanies,
    ready,
    needsSelection,
    selectCompany,
    fetchCompanies,
    reset,
  };
});
